import {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';

import {useCreateCategory} from '@entities/category';
import {useUploadAdminFile} from '@entities/common';
import {getErrorMessageFromApiError} from '@shared/api';
import {CardBox} from '@shared/components';
import {ROUTE__CATEGORIES} from '@shared/constants';
import {resolveEnglishText} from '@shared/helpers';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {FormRow, Header} from '@widgets/common';
import {FormFields} from './FormFields';
import {FormValues, v8nSchema} from './v8nSchema';

import {COLOR__WHITE} from '@/theme/colors';

const emptyTranslations = {} as TranslationDict;

export const CreateCategoryPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const {mutateAsync: createCategory, isPending: isCreating} =
    useCreateCategory();
  const {mutateAsync: uploadAdminFile, isPending: isUploading} =
    useUploadAdminFile();

  const [nameTranslations, setNameTranslations] =
    useState<TranslationDict>(emptyTranslations);
  const [descriptionTranslations, setDescriptionTranslations] =
    useState<TranslationDict>(emptyTranslations);

  const formMethods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      slug: '',
      name: '',
      description: '',
      logoFile: null,
      bannerFile: null,
      order: 0,
    },
  });
  const {
    handleSubmit,
    setValue,
    formState: {dirtyFields},
  } = formMethods;

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      const uploadedLogo = values.logoFile
        ? await uploadAdminFile({file: values.logoFile, type: 'category_logo'})
        : null;

      const uploadedBanner = values.bannerFile
        ? await uploadAdminFile({
            file: values.bannerFile,
            type: 'category_banner',
          })
        : null;

      await createCategory({
        slug: values.slug,
        nameTrans: {
          ...nameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        descriptionTrans: {
          ...descriptionTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            descriptionTranslations,
            values.description,
            !!dirtyFields.description,
          ),
        },
        logoUrl: uploadedLogo?.key ?? undefined,
        bannerUrl: uploadedBanner?.key ?? undefined,
        order: values.order,
      });

      enqueueSnackbar('Category created successfully', {variant: 'success'});
      navigate(ROUTE__CATEGORIES);
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';
      enqueueSnackbar(`Could not create category. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Header title="Create category" mb={1} />

      <CardBox bgcolor={COLOR__WHITE} maxWidth={600}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormProvider {...formMethods}>
            <FormFields
              nameTranslations={nameTranslations}
              onNameTranslationsChange={translations => {
                setValue('name', translations[SupportedLanguage.English]);
                setNameTranslations(translations);
              }}
              descriptionTranslations={descriptionTranslations}
              onDescriptionTranslationsChange={translations => {
                setValue(
                  'description',
                  translations[SupportedLanguage.English],
                );
                setDescriptionTranslations(translations);
              }}
            />
          </FormProvider>

          <FormRow width={420} mt={2}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
            >
              Back
            </Button>

            <Button
              type="submit"
              sx={{flex: 2}}
              disabled={isCreating || isUploading}
            >
              Create category
            </Button>
          </FormRow>
        </form>
      </CardBox>
    </>
  );
};
